import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { ErrorDialogPayload } from '@/stores/errorDialog';

/**
 * Global "failed backend response" handling. An axios response-error
 * interceptor (installed in `httpClient.ts`) derives a calm, human-readable
 * `{ header, message, detail }` from any 4xx/5xx response or network failure
 * and forwards it to the error-dialog store. The error is always re-rejected
 * so existing per-call `.catch` / TanStack `onError` handlers still run — the
 * dialog is purely additive.
 *
 * The generic copy (header / message) is resolved through vue-i18n; the
 * backend `detail` passes through untranslated and is never used as a key.
 */

/** Minimal translator contract — matches vue-i18n's `i18n.global.t`. */
export type Translate = (key: string) => string;

/**
 * Extra axios request-config flag opting a single call out of the global
 * dialog. The error is still re-rejected so inline handlers keep working;
 * only the `show()` side effect is skipped. Set it where a call site already
 * renders its own bespoke error UI:
 *   `httpClient.get(url, { skipGlobalError: true })`
 */
export interface GlobalErrorConfig {
  skipGlobalError?: boolean;
}

const I18N_PREFIX = 'v2.errors.dialog';

/**
 * Bucket an HTTP status (or `undefined` for a network/no-response failure)
 * into the i18n sub-key whose `.header` / `.message` describe it.
 * @param status The response status, or `undefined` when there is no response.
 * @returns The i18n bucket name under `v2.errors.dialog.*`.
 */
export function statusBucket(status: number | undefined): string {
  if (status === undefined) return 'network';
  if (status === 401 || status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status >= 500) return 'server';
  if (status >= 400) return 'badRequest';
  // 2xx/3xx never reach the error path; treat anything else as a generic 4xx.
  return 'badRequest';
}

/**
 * Pull a concise, human-meaningful detail string out of a backend error body.
 * Handles the common OptimalHR / Express shapes (`{ message }`, `{ error }`,
 * `{ detail }`, a bare string body) and falls back to a terse
 * `METHOD url → status` line. Never returns a stack trace; the result is
 * trimmed and length-capped so we don't dump a wall of text or leak PII-heavy
 * payloads into the dialog.
 * @param error The axios error.
 * @returns A short detail string, or `undefined` if nothing useful is present.
 */
export function deriveDetail(error: AxiosError): string | undefined {
  const data = error.response?.data as unknown;

  let raw: string | undefined;
  if (typeof data === 'string') {
    raw = data;
  } else if (data && typeof data === 'object') {
    const body = data as Record<string, unknown>;
    const candidate = body['message'] ?? body['error'] ?? body['detail'];
    if (typeof candidate === 'string') raw = candidate;
    else if (candidate && typeof candidate === 'object') {
      // Some services nest `{ error: { message } }`.
      const nested = (candidate as Record<string, unknown>)['message'];
      if (typeof nested === 'string') raw = nested;
    }
  }

  const cleaned = raw?.replace(/\s+/g, ' ').trim();
  if (cleaned) {
    // Cap to keep the dialog calm and avoid dumping huge bodies / stacks.
    return cleaned.length > 300 ? `${cleaned.slice(0, 297)}…` : cleaned;
  }

  // Fallback: a terse request → status line. Useful for triage, no PII.
  const method = (error.config?.method ?? 'GET').toUpperCase();
  const url = error.config?.url ?? '';
  const status = error.response?.status;
  if (status !== undefined) return `${method} ${url} → ${status}`.trim();
  if (error.code) return `${method} ${url} → ${error.code}`.trim();
  return undefined;
}

/**
 * Build the full `{ header, message, detail }` dialog payload for an axios
 * error, resolving the generic copy through the supplied translator.
 * @param error The axios error.
 * @param t A vue-i18n-style translator.
 * @returns The payload to hand to the error-dialog store's `show()`.
 */
export function buildErrorDialogPayload(error: AxiosError, t: Translate): ErrorDialogPayload {
  const bucket = statusBucket(error.response?.status);
  const header = t(`${I18N_PREFIX}.${bucket}.header`);
  const message = t(`${I18N_PREFIX}.${bucket}.message`);
  const detail = deriveDetail(error);
  return detail ? { header, message, detail } : { header, message };
}

/**
 * Whether this request opted out of the global dialog via `skipGlobalError`.
 * @param config The axios request config attached to the error.
 * @returns `true` when the global dialog should be suppressed for this call.
 */
export function isGlobalErrorSkipped(
  config: (InternalAxiosRequestConfig & GlobalErrorConfig) | undefined,
): boolean {
  return config?.skipGlobalError === true;
}
